import { ReactNode, useEffect, useState } from "react";

import { DateTimePicker } from "@hl/base-components/lib/DateTimePicker";
import {
  Expand2 as FullscreenIcon,
  Refresh as RefreshIcon,
  Shuffle as ShuffleIcon,
  Minimize as MinimizeIcon,
  Pencil,
  AlertTriangle,
} from "@hl/base-components/lib/assets/icons.generated";
import { Expand01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { BACKGROUND_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { maskAddress } from "@hl/communities-app/lib/utils/content";
import { GenArtIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import {
  DEFAULT_MAX_EDITION_SIZE,
  MAX_BLOCK_NUMBER,
  MAX_GAS_PRICE,
  MAX_GAS_USED,
  MAX_MINT_BATCH_SIZE,
  MIN_GAS_PRICE,
  MIN_GAS_USED,
  useGenParams,
} from "@hl/shared-features/lib/features/gen-art/gen-art-utils";
import { usePreviewImage } from "@hl/shared-features/lib/features/gen-art/preview";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Flex,
  Group,
  Loader,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useFullscreen } from "@mantine/hooks";
import dayjs, { extend } from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  CapturePreviewTriggerType,
  DraftProgress,
} from "~/apollo/graphql.generated";

import FormTitle from "../FormTitle";
import NextStepButton from "../NextStepButton";
import SaveDraftButton from "../SaveDraftButton";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "../context";
import useSaveDraft from "../useSaveDraft";

extend(utc);

type Attribute = {
  key: string;
  value: string | number;
};
const toAttributesList = (map: Record<string, string>) =>
  Object.keys(map).map((key) => ({
    key,
    value: map[key],
  }));

type StyleProps = {
  isFullscreen: boolean;
};
const useStyles = createStyles((theme, { isFullscreen }: StyleProps) => ({
  panel: {
    border: `1px solid ${theme.colors.divider[0]}`,
    borderRadius: 6,
  },
  expandBtn: {
    position: "absolute",
    right: 10,
    bottom: 10,
    zIndex: 10,
    height: 32,
    width: 32,
    justifyContent: "center",
    alignItems: "center",
    boxShadow: theme.shadows.xs,
  },
  closeFullscreenBtn: {
    position: "absolute",
    top: 10,
    right: 10,
    boxShadow: theme.shadows.xs,
  },
  iframeContainer: {
    height: isFullscreen ? "100vh" : "auto",
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export const CodeGenTestScript = () => {
  const {
    dispatch,
    state: { fullWidthMode: isExpanded },
  } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.TESTED_GEN_SCRIPT,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const mediaUrl = form.values.metadata.mediaUrl;

  const { params, refreshIframe, randomizeAll, ...genFn } = useGenParams();

  const utcTimestampDate = toLocalUTCDate(new Date(params.timestamp * 1000));

  const {
    ref: fullscreenRef,
    toggle: toggleFullscreen,
    fullscreen: isFullscreen,
  } = useFullscreen();
  const { classes } = useStyles({ isFullscreen });

  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [attributesInvalid, setAttributesInvalid] = useState(false);

  const { loading: isLoadingAttribute, metadata } = usePreviewImage({
    mediaUrl,
    params,
    width: 400,
    height: 400,
    trigger: CapturePreviewTriggerType.Delay,
    delay: 3,
    skipImageCapture: true,
  });

  useEffect(() => {
    const data = metadata?.attributes ?? {};
    const list = toAttributesList(data);
    const isInvalid = list.some(
      (a) => typeof a.key !== "string" || typeof a.value === "object"
    );
    setAttributesInvalid(isInvalid);
    setAttributes(list);
  }, [metadata]);

  if (!mediaUrl) {
    return <div>error: zip with assets is not uploaded</div>;
  }

  return (
    <div>
      {!isExpanded && (
        <>
          <FormTitle
            title="Test script"
            buttons={[
              <SaveDraftButton
                key="draft"
                onClick={() => saveDraft()}
                loading={loading}
              />,
              <NextStepButton
                key="next"
                onClick={onNextStep}
                loading={loading}
              />,
            ]}
          />
          <Text size="sm" maw={728}>
            Next, test your code to ensure it outputs what you expect. You can
            randomize each input individually, or randomize all inputs at once.
            You should also reload the preview with the same inputs to ensure
            the output doesn&rsquo;t change if the inputs don&rsquo;t change.
          </Text>
        </>
      )}
      <Flex pt={40} m={0} gap={64}>
        <Stack sx={{ flexGrow: 1 }} spacing={0}>
          <Group mb={8} position="apart">
            <Text fw={500} size="sm">
              Token preview
            </Text>
            <UnstyledButton onClick={refreshIframe}>
              <Text size="xs" ml={8} fw={500}>
                <Group spacing={8}>
                  <RefreshIcon />
                  Reload with same metadata
                </Group>
              </Text>
            </UnstyledButton>
          </Group>
          <Flex
            pos="relative"
            ref={fullscreenRef}
            className={classes.iframeContainer}
          >
            <GenArtIFrame mediaUrl={mediaUrl} {...params} />
            {!isExpanded && (
              <ActionIcon
                className={classes.expandBtn}
                onClick={() => {
                  dispatch({
                    type: "SET_FULL_WIDTH",
                    payload: { enable: true },
                  });
                }}
              >
                <Expand01 width={16} height={16} />
              </ActionIcon>
            )}
            {isExpanded && !isFullscreen && (
              <Button
                className={classes.expandBtn}
                w="unset"
                color={BACKGROUND_COLOR.SECONDARY}
                onClick={toggleFullscreen}
                leftIcon={<FullscreenIcon />}
              >
                Fullscreen
              </Button>
            )}
            {isFullscreen && (
              <ActionIcon
                className={classes.closeFullscreenBtn}
                onClick={toggleFullscreen}
              >
                <MinimizeIcon />
              </ActionIcon>
            )}
          </Flex>
        </Stack>
        <Stack p={0} spacing={0} w={260}>
          <Group position="apart">
            <Text fw={500} size="sm">
              Inputs
            </Text>
            <UnstyledButton onClick={randomizeAll}>
              <Text size="xs" ml={8} fw={500}>
                <Group spacing={8}>
                  <ShuffleIcon />
                  Randomize all
                </Group>
              </Text>
            </UnstyledButton>
          </Group>
          <Stack spacing={0}>
            <ScrollArea h={226} mt={8} className={classes.panel}>
              <Box px={16}>
                <ParamControl
                  name="Transaction Hash"
                  value={params.txHash}
                  onShuffle={genFn.randomizeTxHash}
                  editProps={{
                    modalType: "hash",
                    hashLength: 64,
                    initialValue: { hash: params.txHash },
                    handleSetNewValue: (values) => genFn.setTxHash(values.hash),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Timestamp"
                  value={dayjs(utcTimestampDate).format("llll")}
                  onShuffle={genFn.randomizeTimestamp}
                  editProps={{
                    modalType: "timestamp",
                    initialValue: {
                      timestamp: utcTimestampDate,
                    },
                    handleSetNewValue: (values) =>
                      genFn.setTimestamp(
                        dayjs(values.timestamp).utc(true).unix()
                      ),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Minting wallet address"
                  value={params.walletAddress}
                  onShuffle={genFn.randomizeMintingWallet}
                  editProps={{
                    modalType: "hash",
                    hashLength: 40,
                    initialValue: { hash: params.walletAddress },
                    handleSetNewValue: (values) =>
                      genFn.setMintingWallet(values.hash),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Token ID"
                  value={params.tokenId}
                  onShuffle={genFn.randomizeTokenId}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.tokenId },
                    handleSetNewValue: (values) =>
                      genFn.setTokenId(values.value),
                    maxValue: DEFAULT_MAX_EDITION_SIZE,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Edition size"
                  value={params.editionSize}
                  onShuffle={genFn.randomizeEditionSize}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.editionSize },
                    handleSetNewValue: (values) =>
                      genFn.setEditionSize(values.value),
                    maxValue: DEFAULT_MAX_EDITION_SIZE,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Contract address"
                  value={params.address}
                  onShuffle={genFn.randomizeAddress}
                  editProps={{
                    modalType: "hash",
                    hashLength: 40,
                    initialValue: { hash: params.address },
                    handleSetNewValue: (values) =>
                      genFn.setAddress(values.hash),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Block Hash"
                  value={params.blockHash}
                  onShuffle={genFn.randomizeBlockHash}
                  editProps={{
                    modalType: "hash",
                    hashLength: 64,
                    initialValue: { hash: params.blockHash },
                    handleSetNewValue: (values) =>
                      genFn.setBlockHash(values.hash),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Block number"
                  value={params.blockNumber}
                  onShuffle={genFn.randomizeBlockNumber}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.blockNumber },
                    handleSetNewValue: (values) =>
                      genFn.setBlockNumber(values.value),
                    maxValue: MAX_BLOCK_NUMBER,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Chain ID"
                  value={params.chainId}
                  onShuffle={genFn.randomizeChainId}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.chainId },
                    handleSetNewValue: (values) =>
                      genFn.setChainId(values.value),
                  }}
                />
                <Divider />
                <ParamControl
                  name="Gas price"
                  value={params.gasPrice}
                  onShuffle={genFn.randomizeGasPrice}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.gasPrice },
                    handleSetNewValue: (values) =>
                      genFn.setGasPrice(values.value),
                    minValue: MIN_GAS_PRICE,
                    maxValue: MAX_GAS_PRICE,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Gas units used"
                  value={params.gasUsed}
                  onShuffle={genFn.randomizeGasUsed}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.gasUsed },
                    handleSetNewValue: (values) =>
                      genFn.setGasUsed(values.value),
                    minValue: MIN_GAS_USED,
                    maxValue: MAX_GAS_USED,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Mint size"
                  value={params.mintSize}
                  onShuffle={genFn.randomizeMintSize}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.mintSize },
                    handleSetNewValue: (values) =>
                      genFn.setMintSize(values.value),
                    maxValue: MAX_MINT_BATCH_SIZE,
                  }}
                />
                <Divider />
                <ParamControl
                  name="Mint iteration"
                  value={params.mintIteration}
                  onShuffle={genFn.randomizeMintIteration}
                  editProps={{
                    modalType: "number",
                    initialValue: { value: params.mintIteration },
                    handleSetNewValue: (values) =>
                      genFn.setMintIteration(values.value),
                    maxValue: MAX_MINT_BATCH_SIZE,
                  }}
                />
              </Box>
            </ScrollArea>
            <div>
              <Text fw={500} mt={32} size="sm">
                Traits
              </Text>
              <ScrollArea h={226} mt={8} className={classes.panel}>
                <Box px={16} py={8} maw={250}>
                  {isLoadingAttribute && (
                    <Center h={150}>
                      <Loader m={20} />
                    </Center>
                  )}
                  {!isLoadingAttribute && attributes.length === 0 && (
                    <Text size="xs" align="center">
                      No Data
                    </Text>
                  )}
                  {!isLoadingAttribute &&
                    (attributesInvalid ? (
                      <Stack h={150} justify="center" align="center">
                        <AlertTriangle />
                        <Text size="xs" fw={500}>
                          Unable to parse traits
                        </Text>
                        <Text size="xs" align="center">
                          Check the object you’re passing to setAttributes() and
                          try again.
                        </Text>
                      </Stack>
                    ) : (
                      attributes.map((attr, index) => (
                        <Stack spacing={0} key={index}>
                          <Text
                            size="xs"
                            fw={WEIGHT_BOLD}
                            className={classes.ellipsisText}
                            title={attr.key}
                          >
                            {attr.key}
                          </Text>
                          <Text
                            size="xs"
                            className={classes.ellipsisText}
                            title={attr.value.toString()}
                          >
                            {attr.value}
                          </Text>
                          {index !== attributes.length - 1 && (
                            <Divider my={8} />
                          )}
                        </Stack>
                      ))
                    ))}
                </Box>
              </ScrollArea>
            </div>
          </Stack>
        </Stack>
      </Flex>
      {!isLoadingAttribute && (metadata?.name || metadata?.description) && (
        <Stack mt={24}>
          {metadata?.name && (
            <Group noWrap align="flex-start">
              <Text size="xs" fw={WEIGHT_BOLD} miw={160}>
                Token name
              </Text>
              <Text size="xs">{metadata?.name}</Text>
            </Group>
          )}
          {metadata?.description && (
            <Group noWrap align="flex-start">
              <Text size="xs" fw={WEIGHT_BOLD} miw={160}>
                Token description
              </Text>
              <Text size="xs">{metadata?.description}</Text>
            </Group>
          )}
        </Stack>
      )}
      {!isExpanded && (
        <NextStepButton
          onClick={onNextStep}
          loading={loading}
          mt={48}
          fullWidth
        />
      )}
    </div>
  );
};

interface TestHashFormType {
  hash: string;
}

interface TestTimestampFormType {
  timestamp: Date;
}

interface TestNumberFormType {
  value: number;
}

interface TestFormProps<T> {
  initialValues: T;
  handleApply(values: T): void;
  onCancel(): void;
}

interface TestHashFormProps extends TestFormProps<TestHashFormType> {
  name: string;
  hashLength: number;
}

type TestTimestampFormProps = TestFormProps<TestTimestampFormType>;
interface TestNumberFormProps extends TestFormProps<TestNumberFormType> {
  name: string;
  minValue?: number;
  maxValue?: number;
}

interface BaseTestFormProps<T> {
  form: UseFormReturnType<T>;
  handleApply(values: T): void;
  onCancel(): void;
  children: ReactNode;
}

const BaseTestForm = <T,>({
  form,
  handleApply,
  onCancel,
  children,
}: BaseTestFormProps<T>) => (
  <form
    onSubmit={form.onSubmit((values) => {
      handleApply(values);
      onCancel();
    })}
  >
    {children}
    <ModalSection>
      <Group noWrap>
        <Button size="xl" fullWidth color={SECONDARY_COLOR} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="xl" fullWidth disabled={!form.isValid()}>
          Apply
        </Button>
      </Group>
    </ModalSection>
  </form>
);

const TestHashForm = ({
  initialValues,
  handleApply,
  onCancel,
  name,
  hashLength,
}: TestHashFormProps) => {
  const form = useForm<TestHashFormType>({
    validateInputOnChange: true,
    initialValues: {
      hash: initialValues.hash,
    },
    validate: {
      hash: (value) =>
        new RegExp(`^0x[a-fA-F0-9]{${hashLength}}$`).test(value)
          ? null
          : `Enter a ${hashLength}-character hexadecimal prefixed with 0x`,
    },
  });

  return (
    <BaseTestForm form={form} handleApply={handleApply} onCancel={onCancel}>
      <Text size="sm">{`Enter the ${name.toLowerCase()} you want to test. Use a ${hashLength}-character hexadecimal prefixed with 0x.`}</Text>
      <TextInput mt="md" label={name} {...form.getInputProps("hash")} />
    </BaseTestForm>
  );
};

const TestTimestampForm = ({
  initialValues,
  handleApply,
  onCancel,
}: TestTimestampFormProps) => {
  const form = useForm<TestTimestampFormType>({
    validateInputOnChange: true,
    validate: {
      timestamp: (value) => (value ? null : "Enter a timestamp"),
    },
    initialValues: {
      timestamp: initialValues.timestamp,
    },
  });

  return (
    <BaseTestForm form={form} handleApply={handleApply} onCancel={onCancel}>
      <Text size="sm">Enter the timestamp you want to test.</Text>
      <DateTimePicker utcDisplay mt="md" {...form.getInputProps("timestamp")} />
    </BaseTestForm>
  );
};

const TestNumberForm = ({
  initialValues,
  handleApply,
  onCancel,
  name,
  minValue = 1,
  maxValue,
}: TestNumberFormProps) => {
  const form = useForm<TestNumberFormType>({
    validateInputOnChange: true,
    initialValues: {
      value: initialValues.value,
    },
    validate: {
      value: (value) =>
        value < minValue || (maxValue && value > maxValue) || !value
          ? maxValue
            ? `Enter an integer from ${minValue.toLocaleString(
                "en-US"
              )} to ${maxValue.toLocaleString("en-US")}`
            : `Enter an integer greater than or equal to ${minValue.toLocaleString(
                "en-US"
              )}`
          : null,
    },
  });

  return (
    <BaseTestForm form={form} handleApply={handleApply} onCancel={onCancel}>
      <Text size="sm">
        Enter the {name.toLowerCase()} you want to test. Use any integer{" "}
        {maxValue
          ? `from ${minValue.toLocaleString(
              "en-US"
            )} to ${maxValue.toLocaleString("en-US")}`
          : `greater than or equal to ${minValue.toLocaleString("en-US")}`}
        .
      </Text>
      <NumberInput
        mt="md"
        label={`Test a ${name}`}
        {...form.getInputProps("value")}
      />
    </BaseTestForm>
  );
};

const ParamControl = ({
  name,
  value,
  onShuffle,
  editProps,
}: {
  name: string;
  value: string | number;
  onShuffle: () => void;
  editProps:
    | {
        initialValue: TestHashFormType;
        handleSetNewValue(values: TestHashFormType): void;
        hashLength: number;
        modalType: "hash";
      }
    | {
        initialValue: TestTimestampFormType;
        handleSetNewValue(values: TestTimestampFormType): void;
        modalType: "timestamp";
      }
    | {
        initialValue: TestNumberFormType;
        handleSetNewValue(values: TestNumberFormType): void;
        minValue?: number;
        maxValue?: number;
        modalType: "number";
      };
}) => {
  const { pushModal, popModal } = useModalStack();

  const openModal = (title: string, modal: ReactNode) =>
    pushModal(modal, {
      size: 680,
      title: (
        <Text size="lg" weight={WEIGHT_BOLD}>
          {title}
        </Text>
      ),
    });

  const openTestModal = () => {
    const { modalType, initialValue, handleSetNewValue } = editProps;
    if (modalType === "hash") {
      openModal(
        `Test a ${name.toLowerCase()}`,
        <TestHashForm
          name={name}
          hashLength={editProps.hashLength}
          initialValues={initialValue}
          handleApply={handleSetNewValue}
          onCancel={popModal}
        />
      );
    } else if (modalType === "timestamp") {
      openModal(
        "Test a timestamp",
        <TestTimestampForm
          initialValues={initialValue}
          handleApply={handleSetNewValue}
          onCancel={popModal}
        />
      );
    } else {
      openModal(
        `Test a ${name.toLowerCase()}`,
        <TestNumberForm
          initialValues={initialValue}
          handleApply={handleSetNewValue}
          onCancel={popModal}
          name={name}
          minValue={editProps.minValue}
          maxValue={editProps.maxValue}
        />
      );
    }
  };

  return (
    <Group align="center" position="apart" my={8}>
      <Stack spacing={0}>
        <Text size="xs" fw={WEIGHT_BOLD}>
          {name}
        </Text>
        <Text size="xs">
          {typeof value === "string" && value.length > 30
            ? maskAddress(value, 6, 14)
            : value}
        </Text>
      </Stack>
      <Group spacing={0}>
        <ActionIcon size={20} onClick={openTestModal}>
          <Pencil />
        </ActionIcon>
        <ActionIcon size={20} onClick={onShuffle}>
          <ShuffleIcon />
        </ActionIcon>
      </Group>
    </Group>
  );
};

/**
 * Creates a new Date with UTC as its local time.
 */
const toLocalUTCDate = (date: Date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
